import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    KeyboardSensor,
    type DragEndEvent,
    type DragStartEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { memo, useId, useState, type FC } from "react";
import { Plus } from "lucide-react";
import { EnvironmentClassItem } from "./EnvironmentClassItem";
import { EnvironmentClassSelect } from "./EnvironmentClassSelect";

type EnvironmentClassOrderedListProps = {
    environmentClassEntries?: EnvironmentClassEntry[];
    allEnvironmentClassEntries?: EnvironmentClassEntry[];
    label: string;
    loading?: boolean;
    disabled?: boolean;
    minItems?: number;
    maxItems?: number;
    onChange: (reorderedEntries: EnvironmentClassEntry[]) => void;
    showPrebuildColumn?: boolean;
    prebuildSelectedIds?: string[];
    onPrebuildSelectionChange?: (ids: string[]) => void;
};

export const EnvironmentClassOrderedList: FC<EnvironmentClassOrderedListProps> = memo(
    ({
        environmentClassEntries,
        allEnvironmentClassEntries,
        label,
        loading,
        disabled,
        minItems,
        maxItems,
        onChange,
        showPrebuildColumn,
        prebuildSelectedIds,
        onPrebuildSelectionChange,
    }) => {
        const elId = useId();
        const [activeId, setActiveId] = useState<string | null>(null);

        const sensors = useSensors(
            useSensor(PointerSensor, {
                activationConstraint: {
                    distance: 8,
                },
            }),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            }),
        );

        const handleDragStart = (event: DragStartEvent) => {
            const { active } = event;
            setActiveId(active.id as string);
        };

        const handleDragEnd = (event: DragEndEvent) => {
            if (!environmentClassEntries) {
                return;
            }

            const { active, over } = event;
            setActiveId(null);

            if (!over || active.id === over.id) {
                return;
            }

            const oldIndex = environmentClassEntries.findIndex(
                (entry) => `${entry.clazz.runnerId}:${entry.clazz.id}` === active.id,
            );
            const newIndex = environmentClassEntries.findIndex(
                (entry) => `${entry.clazz.runnerId}:${entry.clazz.id}` === over.id,
            );

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                onChange(arrayMove(environmentClassEntries, oldIndex, newIndex));
            }
        };

        const activeEntry = activeId
            ? environmentClassEntries?.find((entry) => `${entry.clazz.runnerId}:${entry.clazz.id}` === activeId)
            : undefined;

        // Filter available environment classes (exclude ones already in the list)
        const availableEnvironmentClasses =
            allEnvironmentClassEntries && environmentClassEntries
                ? allEnvironmentClassEntries.filter(
                      (entry) =>
                          !environmentClassEntries.some(
                              (existingEntry) =>
                                  existingEntry.clazz.runnerId === entry.clazz.runnerId &&
                                  existingEntry.clazz.id === entry.clazz.id,
                          ),
                  )
                : [];

        const handleAddEnvironmentClass = (entry: EnvironmentClassEntry | undefined) => {
            if (!environmentClassEntries || !entry) {
                return;
            }
            // Check if we're at the maximum number of items
            if (maxItems && environmentClassEntries.length >= maxItems) {
                return;
            }
            onChange([...environmentClassEntries, entry]);
        };

        const handleRemoveEnvironmentClass = (entryToRemove: EnvironmentClassEntry) => {
            if (!environmentClassEntries) {
                return;
            }
            // Check if we're at the minimum number of items
            if (minItems && environmentClassEntries.length <= minItems) {
                return;
            }
            const filteredEntries = environmentClassEntries.filter(
                (entry) =>
                    !(
                        entry.clazz.runnerId === entryToRemove.clazz.runnerId &&
                        entry.clazz.id === entryToRemove.clazz.id
                    ),
            );
            onChange(filteredEntries);
        };

        const handlePrebuildToggle = (envClassId: string, checked: boolean) => {
            if (!onPrebuildSelectionChange || !prebuildSelectedIds) return;
            if (checked) {
                onPrebuildSelectionChange([...prebuildSelectedIds, envClassId]);
            } else {
                onPrebuildSelectionChange(prebuildSelectedIds.filter((id) => id !== envClassId));
            }
        };

        const currentCount = environmentClassEntries?.length || 0;
        const isAtMaxItems = maxItems ? currentCount >= maxItems : false;
        const isAtMinItems = minItems ? currentCount <= minItems : false;
        const showEnvironmentClassSelect =
            allEnvironmentClassEntries && availableEnvironmentClasses.length > 0 && !isAtMaxItems;

        return (
            <div className="flex w-full flex-col gap-2">
                <Label htmlFor={elId} className="font-medium">
                    {label}
                </Label>

                {loading || !environmentClassEntries ? (
                    <div className="flex items-center justify-center rounded-lg border border-border-base bg-surface-pure px-5 py-4">
                        <span className="text-content-tertiary">Loading environment classes...</span>
                    </div>
                ) : environmentClassEntries.length === 0 ? (
                    <div className="flex items-center justify-center rounded-lg border border-border-base bg-surface-pure px-5 py-4">
                        <span className="text-content-tertiary">No environment classes</span>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    >
                        <div
                            id={elId}
                            className={cn(
                                "rounded-lg border border-border-base bg-surface-pure",
                                disabled && "pointer-events-none opacity-50",
                            )}
                        >
                            <SortableContext
                                items={environmentClassEntries.map(
                                    (entry) => `${entry.clazz.runnerId}:${entry.clazz.id}`,
                                )}
                                strategy={verticalListSortingStrategy}
                            >
                                {environmentClassEntries.map((environmentClassEntry) => {
                                    const id = `${environmentClassEntry.clazz.runnerId}:${environmentClassEntry.clazz.id}`;
                                    return (
                                        <EnvironmentClassItem
                                            key={id}
                                            id={id}
                                            environmentClassEntry={environmentClassEntry}
                                            disabled={disabled}
                                            isDraggable={true}
                                            className={"border-b border-border-light last:border-b-0"}
                                            onRemove={
                                                isAtMinItems
                                                    ? undefined
                                                    : () => handleRemoveEnvironmentClass(environmentClassEntry)
                                            }
                                            showPrebuildCheckbox={showPrebuildColumn}
                                            isPrebuildSelected={prebuildSelectedIds?.includes(environmentClassEntry.clazz.id)}
                                            onPrebuildToggle={(checked) =>
                                                handlePrebuildToggle(environmentClassEntry.clazz.id, checked)
                                            }
                                        />
                                    );
                                })}
                            </SortableContext>
                        </div>

                        <DragOverlay>
                            {activeEntry && (
                                <div className="rounded-lg border border-border-base bg-surface-primary shadow-lg">
                                    <EnvironmentClassItem
                                        id={`${activeEntry.clazz.runnerId}:${activeEntry.clazz.id}`}
                                        environmentClassEntry={activeEntry}
                                        disabled={disabled}
                                        isDraggable={true}
                                        className="border-b-0 shadow-lg"
                                    />
                                </div>
                            )}
                        </DragOverlay>
                    </DndContext>
                )}

                {showEnvironmentClassSelect && (
                    <EnvironmentClassSelect
                        key={availableEnvironmentClasses.map((e) => `${e.clazz.runnerId}:${e.clazz.id}`).join(",")}
                        environmentClassEntries={availableEnvironmentClasses}
                        loading={false}
                        disabled={disabled || false}
                        name="add-environment-class"
                        onChange={handleAddEnvironmentClass}
                        placeholder={
                            <div className="flex h-14 w-full items-center gap-2 px-6">
                                <Plus size={16} className="text-content-primary" />
                                <span className="text-content-primary">Add environment class</span>
                            </div>
                        }
                    />
                )}
            </div>
        );
    },
);

EnvironmentClassOrderedList.displayName = "EnvironmentClassOrderedList";
